import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "ProgramasFormacion";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarProgramas = ({ refresh }) => {
  const [programas, setProgramas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO PROGRAMAS
  // ==============================
  const getModuloProgramas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloProgramas = useMemo(
    () => getModuloProgramas(),
    []
  );

  const puedeCrear =
    moduloProgramas?.puedeCrear === true ||
    moduloProgramas?.crear === true ||
    moduloProgramas?.create === true;

  const puedeActualizar =
    moduloProgramas?.puedeActualizar === true ||
    moduloProgramas?.actualizar === true ||
    moduloProgramas?.editar === true ||
    moduloProgramas?.update === true;

  const puedeEliminar =
    moduloProgramas?.puedeEliminar === true ||
    moduloProgramas?.eliminar === true ||
    moduloProgramas?.delete === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");

          navigate("/", {
            replace: true,
          });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // ELIMINAR PROGRAMA
  // ==============================
  const deletePrograma = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar programas.",
          "warning"
        );

        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(id);

        const { data } = await axios.delete(
          `/api/programas/delete/${id}`,
          authConfig()
        );

        setProgramas((prev) =>
          prev.filter(
            (programa) => programa._id !== id
          )
        );

        Swal.fire({
          icon: "success",
          title:
            data?.message ||
            "Programa eliminado",
          showConfirmButton: false,
          timer: 1500,
        });

        return true;
      } catch (error) {
        console.error(
          "Error al eliminar programa:",
          error
        );

        if (manejarErrorSesion(error))
          return false;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            "No se pudo eliminar el programa.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // CARGAR DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        "/api/programas/listall/",
        authConfig()
      );

      const lista = Array.isArray(
        data?.programas
      )
        ? data.programas
        : [];

      const programasOrdenados = [
        ...lista,
      ].reverse();

      setProgramas(programasOrdenados);
    } catch (error) {
      console.error(
        "Error al cargar programas:",
        error
      );

      if (manejarErrorSesion(error))
        return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "No se pudieron cargar los programas.",
        "error"
      );

      setProgramas([]);
    } finally {
      setLoading(false);
    }
  }, [manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // ==============================
  // DATA TABLE
  // ==============================
  const dataTabla = useMemo(
    () =>
      programas.map((programa) => ({
        ...programa,

        nombrePrograma: (
          <div
            className="text-truncate"
            style={{
              maxWidth: 260,
            }}
            title={
              programa.nombrePrograma
            }
          >
            {programa.nombrePrograma}
          </div>
        ),

        funciones: (
          <div className="d-flex gap-2 flex-wrap justify-content-end">

            {/* =========================
                GUÍAS
            ========================= */}

            <button
              className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
              style={{
                width: 34,
                height: 34,
              }}
              onClick={() =>
                navigate(
                  `/institucional/guias/listarguias/${programa.codPrograma}`
                )
              }
              title="Listar Guías del Programa"
              type="button"
            >
              <i className="fa-solid fa-list-check"></i>
            </button>

            {/* =========================
                EDITAR
            ========================= */}

            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{
                  width: 34,
                  height: 34,
                }}
                disabled={
                  loading ||
                  deletingId ===
                    programa._id
                }
                onClick={() =>
                  navigate(
                    `/institucional/programas/edit/${programa._id}`
                  )
                }
                title="Editar Programa"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {/* =========================
                ELIMINAR
            ========================= */}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{
                  width: 34,
                  height: 34,
                }}
                disabled={
                  loading ||
                  deletingId ===
                    programa._id
                }
                onClick={() =>
                  deletePrograma(
                    programa._id
                  )
                }
                title="Eliminar Programa"
                type="button"
              >
                {deletingId ===
                programa._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      programas,
      navigate,
      loading,
      deletingId,
      puedeActualizar,
      puedeEliminar,
      deletePrograma,
    ]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Código",
        accessor: "codPrograma",
      },

      {
        Header: "Programa",
        accessor: "nombrePrograma",
      },

      {
        Header: "Estado",
        accessor: "estadoPrograma",
      },

      {
        Header: "Nivel",
        accessor: "nivelPrograma",
      },

      {
        Header: "Acciones",
        accessor: "funciones",
      },
    ],
    []
  );

  // ==============================
  // RENDER
  // ==============================
  return (
    <div className="bg-body-tertiary py-4">

      <div className="container-fluid px-2 px-md-4">

        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* =========================
              HEADER
          ========================= */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Programas de Formación
                </h5>

                <small className="text-secondary">
                  Gestión institucional y administración de programas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() =>
                      navigate(
                        "/institucional/programas/nuevo"
                      )
                    }
                    title="Crear Programa"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* =========================
              BODY
          ========================= */}

          <div className="p-3 p-md-4">

            {loading ? (
              <div className="text-center text-secondary py-5">

                <div
                  className="spinner-border"
                  role="status"
                />

                <div className="mt-3">
                  Cargando programas…
                </div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">

                <DataTable
                  columns={columns}
                  data={dataTabla}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  📚
                </div>

                <div className="fw-semibold">
                  No hay programas de formación registrados
                </div>

                <div className="small mb-3">
                  Crea el primer programa para iniciar la gestión.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(
                        "/institucional/programas/nuevo"
                      )
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear programa
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarProgramas;